import { z } from 'zod';
import { isRealDate, paginationQuerySchema, uuidSchema } from './common.js';
const text = (max) => z
    .string()
    .trim()
    .max(max)
    .refine((v) => !v.includes('\u0000'), 'Invalid character');
const optionalText = (max) => text(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine(isRealDate, 'Invalid calendar date');
const atLeastOne = (v) => Object.keys(v).length > 0;
/** Multipart fields arrive as text: "true" and "false" are the only booleans. */
const formFlag = z.union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')]);
// ----------------------------------------------------------------- documents
export const DOCUMENT_CATEGORIES = [
    'birth_certificate',
    'id_proof',
    'address_proof',
    'transfer_certificate',
    'medical',
    'photo',
    'qualification',
    'contract',
    'other',
];
/** The kinds of file a school may keep, with the bytes that identify each one. */
export const DOCUMENT_MIME_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
/** The text fields sent beside the file: whose document it is, what kind, and who may see it. */
export const uploadDocumentFieldsSchema = z
    .object({
    studentId: uuidSchema.optional(),
    staffId: uuidSchema.optional(),
    category: z.enum(DOCUMENT_CATEGORIES),
    title: text(200).optional(),
    visibleToParents: formFlag.default(false),
})
    .refine((v) => (v.studentId === undefined) !== (v.staffId === undefined), {
    message: 'Give either a student or a staff member',
    path: ['studentId'],
})
    .refine((v) => !v.visibleToParents || v.staffId === undefined, {
    message: 'Only a student’s documents can be shown to parents',
    path: ['visibleToParents'],
});
export const updateDocumentSchema = z
    .object({
    title: text(200).min(1),
    category: z.enum(DOCUMENT_CATEGORIES),
    visibleToParents: z.boolean(),
})
    .partial()
    .refine(atLeastOne, 'At least one field is required');
export const reviewDocumentSchema = z
    .object({ decision: z.enum(['verified', 'rejected']), note: optionalText(300) })
    .refine((v) => v.decision === 'verified' || !!v.note, {
    message: 'Say why the document was rejected',
    path: ['note'],
});
export const listDocumentsQuerySchema = paginationQuerySchema
    .extend({
    studentId: uuidSchema.optional(),
    staffId: uuidSchema.optional(),
    category: z.enum(DOCUMENT_CATEGORIES).optional(),
    status: z.enum(['pending', 'verified', 'rejected']).optional(),
})
    .refine((v) => !(v.studentId && v.staffId), {
    message: 'Ask for one owner at a time',
    path: ['studentId'],
});
// -------------------------------------------------------------- certificates
export const CERTIFICATE_TYPES = ['bonafide', 'character', 'transfer', 'fee_clearance'];
/** What a staff member may add when issuing, beyond what the records already say. */
const certificateDetails = {
    purpose: optionalText(300),
    /** A Latin-letters spelling of the name, for a student whose name the PDF fonts cannot print. */
    printedName: optionalText(200),
    leavingDate: isoDate.nullable().optional(),
    reason: optionalText(300),
    conduct: optionalText(100),
};
export const issueCertificateSchema = z.object({
    studentId: uuidSchema,
    type: z.enum(CERTIFICATE_TYPES),
    ...certificateDetails,
});
export const revokeCertificateSchema = z.object({ reason: text(300).min(1) });
export const listCertificatesQuerySchema = paginationQuerySchema.extend({
    type: z.enum(CERTIFICATE_TYPES).optional(),
    status: z.enum(['issued', 'revoked']).optional(),
    studentId: uuidSchema.optional(),
    q: text(50).optional(),
});
export const requestCertificateSchema = z.object({
    type: z.enum(CERTIFICATE_TYPES),
    purpose: text(300).min(1),
});
export const decideRequestSchema = z
    .object({
    decision: z.enum(['approve', 'reject']),
    note: optionalText(300),
    printedName: certificateDetails.printedName,
    leavingDate: certificateDetails.leavingDate,
    reason: certificateDetails.reason,
    conduct: certificateDetails.conduct,
})
    .refine((v) => v.decision === 'approve' || !!v.note, {
    message: 'Say why the request was declined',
    path: ['note'],
});
export const listRequestsQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['pending', 'approved', 'rejected', 'cancelled']).optional(),
});
/** The code printed on a certificate: 12 characters without the look-alikes 0, 1, I, O. */
export const VERIFICATION_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const verificationCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-HJ-NP-Z2-9]{12}$/, 'Not a valid verification code');
/** Whether the built-in PDF fonts can print every character of the text (they cover Latin-1). */
export const isPrintableLatin1 = (value) => /^[ -~¡-ÿ]*$/.test(value);
/** "Ann Reader" -> "A** R*****": enough for the holder to recognise their own certificate, not for a stranger to learn a name. */
export function maskName(name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => `${part[0] ?? ''}${'*'.repeat(Math.max(part.length - 1, 0))}`)
        .join(' ');
}
//# sourceMappingURL=documents.js.map